// TRANSFERS (owner 2026-10-29: "a gate/tab that shows what is currently
// uploading/downloading and what is queued, with cancel and pause").
//
// Every model file moving between the project and ACC / Procore: downloads into Models
// (the sync, a tunnel) and uploads out of it (an approval, a send, a tunnel). Each row can
// be paused, resumed, cancelled or retried; "Pause all" shuts the project's gate so nothing
// starts until it is opened again (back/utils/transfers). The list refreshes on its own
// while the tab is open.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Popconfirm, Progress, Segmented, Space, Table, Tag, Tooltip } from "antd";
import {
  CloudDownloadOutlined, CloudUploadOutlined, PauseCircleOutlined, PlayCircleOutlined, StopOutlined, RedoOutlined, ReloadOutlined,
} from "@ant-design/icons";
import moment from "moment";
import addNotification from "helpers/notify";
import { AutodeskLogo, ProcoreLogo } from "components/Integrations/ProviderLogos";
import { listTransfers, pauseAllTransfers, resumeAllTransfers, transferAction } from "api/projectModels";

const POLL_MS = 4000;
const STATUS = {
  running: { color: "processing", label: "In progress" },
  queued: { color: "blue", label: "Waiting" },
  paused: { color: "gold", label: "Paused" },
  failed: { color: "red", label: "Failed" },
  cancelled: { color: "default", label: "Cancelled" },
  done: { color: "green", label: "Done" },
};
const VIA = { tunnel: "Tunnel", sync: "Sync", approval: "Approval" };
const ACTIVE = ["running", "queued", "paused"];

const size = (b) => {
  if (!b) return "";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(n < 10 && i ? 1 : 0)} ${u[i]}`;
};

export default function ModelTransfersTab({ projectId, active, onCounts }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [show, setShow] = useState("active");
  const [busy, setBusy] = useState("");
  const timer = useRef(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const d = await listTransfers(projectId);
      setData(d);
      setError("");
      if (onCounts) onCounts(d.counts || {});
    } catch (e) {
      setError(e.message);
    }
  }, [projectId, onCounts]);

  useEffect(() => { setData(null); load(); }, [load]);
  // refresh while the tab is open; once on its own when it is not, for the tab's badge
  useEffect(() => {
    if (!active) return undefined;
    timer.current = setInterval(load, POLL_MS);
    return () => clearInterval(timer.current);
  }, [active, load]);

  const act = async (row, action) => {
    setBusy(`${row.id}:${action}`);
    try {
      const d = await transferAction(projectId, row.type, row.id, action);
      setData(d);
      if (onCounts) onCounts(d.counts || {});
    } catch (e) {
      addNotification(e.message, "danger");
    }
    setBusy("");
  };

  const gate = async (pause) => {
    setBusy(pause ? "pause-all" : "resume-all");
    try {
      const d = await (pause ? pauseAllTransfers(projectId) : resumeAllTransfers(projectId));
      setData(d);
      if (onCounts) onCounts(d.counts || {});
      addNotification(pause ? "All transfers on this project are paused" : "Transfers resumed", "success");
    } catch (e) {
      addNotification(e.message, "danger");
    }
    setBusy("");
  };

  const items = (data && data.items) || [];
  const shown = show === "active" ? items.filter((i) => ACTIVE.includes(i.status)) : items;
  const counts = (data && data.counts) || {};

  const columns = [
    {
      title: "",
      key: "type",
      width: 110,
      render: (_, r) => (r.type === "download"
        ? <span className="transfer-dir"><CloudDownloadOutlined /> Download</span>
        : <span className="transfer-dir"><CloudUploadOutlined /> Upload</span>),
    },
    {
      title: "File",
      key: "name",
      // sorted by path, then type, then name, like the Files tab (owner 2026-11-04)
      sorter: { compare: (a, b) => String(a.name || "").localeCompare(String(b.name || ""), undefined, { numeric: true }), multiple: 1 },
      defaultSortOrder: "ascend",
      render: (_, r) => (
        <span>
          <b>{r.name}</b>
          {r.version ? <small className="text-muted"> · v{r.version}</small> : null}
          {r.bytes ? <small className="text-muted"> · {size(r.bytes)}</small> : null}
        </span>
      ),
    },
    {
      // BOTH ENDS, NAMED (owner 2026-11-04: the path column states from path -> to path).
      // A download comes from the provider folder into this project's Models; an upload
      // leaves the Models for the provider folder.
      title: "Path (from -> to)",
      key: "where",
      sorter: { compare: (a, b) => String(a.fromPath || a.path || "").localeCompare(String(b.fromPath || b.path || "")), multiple: 3 },
      defaultSortOrder: "ascend",
      render: (_, r) => {
        const Logo = r.provider === "procore" ? ProcoreLogo : AutodeskLogo;
        const from = r.fromPath || (r.type === "download" ? r.path : "BidLight / Models");
        const to = r.toPath || (r.type === "download" ? "BidLight / Models" : r.path);
        return (
          <span className="models-source">
            <Logo connected size={16} />
            <span>
              <b>{r.type === "download" ? `From ${r.platform}` : `To ${r.platform}`}</b>
              <small className="d-block text-muted">
                <span className="text-muted">From: </span>
                <span className="models-path" title={from}>{from || "-"}</span>
              </small>
              <small className="d-block text-muted">
                <span className="text-muted">to: </span>
                <span className="models-path" title={to}>{to || "-"}</span>
              </small>
            </span>
          </span>
        );
      },
    },
    {
      title: "Started by",
      key: "via",
      width: 150,
      sorter: (a, b) => String(a.via || "").localeCompare(String(b.via || "")),
      render: (_, r) => (
        <Tooltip title={r.tunnelName ? `Model tunnel: ${r.tunnelName}` : r.via === "sync" ? "A sync of the project's model folders" : r.via === "approval" ? "An approved version going to its destinations" : ""}>
          <span>
            <Tag>{VIA[r.via] || r.via}</Tag>
            {r.tunnelName ? <small className="d-block text-muted models-path" title={r.tunnelName}>{r.tunnelName}</small> : null}
          </span>
        </Tooltip>
      ),
    },
    {
      // the row's own creation, which is not when this transfer started (owner 2026-11-04)
      title: "Created",
      key: "createdAt",
      width: 120,
      sorter: (a, b) => new Date(a.createdAt || a.at || 0) - new Date(b.createdAt || b.at || 0),
      render: (_, r) => (r.createdAt || r.at ? (
        <Tooltip title={moment(r.createdAt || r.at).format("YYYY-MM-DD HH:mm")}>
          <small className="text-muted">{moment(r.createdAt || r.at).format("YYYY-MM-DD HH:mm")}</small>
        </Tooltip>
      ) : null),
    },
    {
      title: "Status",
      key: "status",
      width: 220,
      sorter: (a, b) => String(a.status || "").localeCompare(String(b.status || "")),
      render: (_, r) => {
        const s = STATUS[r.status] || { color: "default", label: r.status };
        return (
          <div>
            <Tag color={s.color}>{r.stopping ? (r.stopping === "cancel" ? "Cancelling..." : "Pausing...") : s.label}</Tag>
            {r.status === "running" ? <Progress percent={r.progress || 0} size="small" status="active" /> : null}
            {r.error && r.status === "failed" ? <small className="d-block text-danger" title={r.error}>{r.error.slice(0, 120)}</small> : null}
            <small className="d-block text-muted">{moment(r.finishedAt || r.at).fromNow()}</small>
          </div>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 200,
      render: (_, r) => {
        const b = (action) => busy === `${r.id}:${action}`;
        const stopping = !!r.stopping;
        return (
          <Space size={4} wrap>
            {["running", "queued"].includes(r.status) ? (
              <Tooltip title={r.status === "running" ? "Stop this transfer within a few seconds and hold it as Paused - it keeps its place" : "Hold this transfer: it will not start until it is resumed"}>
                <Button size="small" icon={<PauseCircleOutlined />} loading={b("pause")} disabled={stopping} onClick={() => act(r, "pause")}>Pause</Button>
              </Tooltip>
            ) : null}
            {r.status === "paused" ? (
              <Tooltip title={data && data.paused ? "Every transfer on this project is paused - resume them all first" : "Queue this transfer again"}>
                <Button size="small" icon={<PlayCircleOutlined />} loading={b("resume")} disabled={data && data.paused} onClick={() => act(r, "resume")}>Resume</Button>
              </Tooltip>
            ) : null}
            {["running", "queued", "paused", "failed"].includes(r.status) ? (
              <Tooltip title={r.type === "download"
                ? "Give this download up. A file with earlier versions here keeps them; a first download leaves nothing and comes back only with a new version at the source."
                : "Give this upload up. Nothing is changed on the destination."}>
                <Popconfirm title={`Cancel this ${r.type}?`} okText="Cancel it" cancelText="Keep" onConfirm={() => act(r, "cancel")}>
                  <Button size="small" danger icon={<StopOutlined />} loading={b("cancel")} disabled={stopping}>Cancel</Button>
                </Popconfirm>
              </Tooltip>
            ) : null}
            {["failed", "cancelled"].includes(r.status) ? (
              <Tooltip title={r.error ? `Try again: ${r.error}` : "Queue this transfer again"}>
                <Button size="small" icon={<RedoOutlined />} loading={b("retry")} disabled={data && data.paused} onClick={() => act(r, "retry")}>Retry</Button>
              </Tooltip>
            ) : null}
          </Space>
        );
      },
    },
  ];

  return (
    <div className="models-transfers" data-testid="models-transfers">
      {data && data.paused ? (
        <Alert
          type="warning"
          showIcon
          className="mb-2"
          message={`All transfers on this project are paused${data.pausedAt ? ` (since ${moment(data.pausedAt).format("MMM D, HH:mm")})` : ""}`}
          description="Nothing downloads from or uploads to ACC / Procore until you resume. Anything new is held as Paused."
          action={(
            <Tooltip title="Open the gate and queue every paused download and upload again">
              <Button type="primary" icon={<PlayCircleOutlined />} loading={busy === "resume-all"} onClick={() => gate(false)}>Resume all</Button>
            </Tooltip>
          )}
        />
      ) : null}
      <div className="models-toolbar">
        <Tooltip title="Active: what is moving, waiting or paused. Last 24 hours: those too, with what finished, failed or was cancelled since yesterday.">
          <Segmented
            value={show}
            onChange={setShow}
            options={[
              { value: "active", label: `Active (${(counts.running || 0) + (counts.queued || 0) + (counts.paused || 0)})` },
              { value: "all", label: "Last 24 hours" },
            ]}
          />
        </Tooltip>
        <small className="text-muted">
          {counts.running || 0} in progress · {counts.queued || 0} waiting · {counts.paused || 0} paused
          {counts.failed ? ` · ${counts.failed} failed` : ""}
        </small>
        <span className="models-spacer" />
        <Tooltip title="Read the transfers again now (the list refreshes itself every few seconds)">
          <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
        </Tooltip>
        {data && !data.paused ? (
          <Tooltip title="Shut this project's gate: nothing downloads from or uploads to ACC / Procore - including a tunnel's own checks - until it is opened again">
            <Popconfirm
              title="Pause every download and upload on this project?"
              description="Running transfers stop within a few seconds; waiting ones are held."
              okText="Pause all"
              onConfirm={() => gate(true)}
            >
              <Button danger icon={<PauseCircleOutlined />} loading={busy === "pause-all"} data-testid="transfers-pause-all">Pause all</Button>
            </Popconfirm>
          </Tooltip>
        ) : null}
      </div>
      {error ? <p className="text-danger">{error}</p> : null}
      <Table
        rowKey={(r) => `${r.type}:${r.id}`}
        size="small"
        loading={data === null}
        dataSource={shown}
        columns={columns}
        pagination={{ pageSize: 50, hideOnSinglePage: true }}
        scroll={{ x: 1200 }}
        locale={{ emptyText: show === "active" ? "Nothing is downloading, uploading or waiting." : "No transfers in the last 24 hours." }}
      />
    </div>
  );
}
